import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Switch,
  Select,
  FormControl,
  Typography,
  IconButton,
  Grid,
  Box,
  TextField,
  Autocomplete,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import { AlertComponent, ConfirmationPopup, TextInputComponent } from "../../../../../../../common/components";
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  MEETING_DIALOG_INITIAL_VALUES,
  MEETING_DIALOG_VALIDATION_SCHEMA,
  getFullName,
} from "./common-dialog-helper";
import SearchableSelectOwner from "../../../../../../../common/components/select-owner/select-owner-compoent";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import SelectInputComponent from "../../../../../../../common/components/select-input-component/select-input-component";
import dayjs from "dayjs";
import "./common-dialog-meeting.scss";
import { getUserDetails } from "../../../../../../../api/lead";
import SearchIcon from "@mui/icons-material/Search";
import AddToCampaignModal from "../../../../../../leads/lead-actions/add-new-campaign/add-new-campiagn";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Button } from "reactstrap";
import { useFormikContext } from 'formik';
import ParticipantsDialog from "./create-participant-dialog";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import { createMeeting, editMeeting } from "../../../../../../../api/campaign";
import { getCombinedDateTime } from "../../../../../../../utils/utils";
import AllDayAutoFillEffect from "./allday-autofill";
import ConfirmIcon from "../../../../../../../assets/icons/CreateLeadCapturePopupCancelIcon.svg";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';


interface CommonMeetingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  leadDetails?: any;
  initialMeeting?: any;
  setOpenMeetingDialog?: any;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}
const CommonMeetingDialog: React.FC<CommonMeetingDialogProps> = ({
  open,
  onClose,
  onSave,
  leadDetails,
  title = "Create Meeting",
  initialMeeting,

}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const [formInitialValues, setFormInitialValues] = useState(
    MEETING_DIALOG_INITIAL_VALUES
  );
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [showOverlapPopup, setShowOverlapPopup] = useState(false);
  const [pendingMeetingPayload, setPendingMeetingPayload] = useState<any>(null);
  const [overlapErrorMessage, setOverlapErrorMessage] = useState("");
  const [formData, setFormData] = useState<any>(null);
  const [participantsDialogOpen, setParticipantsDialogOpen] = React.useState(false);
  useEffect(() => {
    setShowInvitePopup(false);
    setShowOverlapPopup(false);
  }, [open]);
  const now = dayjs();
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  const roundToNext15Min = (time: dayjs.Dayjs) => {
    const minutes = time.minute();
    const remainder = 15 - (minutes % 15);
    return time.add(remainder, 'minute').startOf('minute');
  };

  const currentUserDetails = getCurrentUser();
  const matchedUser = leadOptions.find(
    (opt) => opt.value === String(currentUserDetails?.id)
  );
  const computedInitialValues = useMemo(() => {
    if (initialMeeting) {
      return {
        title: initialMeeting.title || "",
        location: initialMeeting.location || "",
        allDay: initialMeeting.all_day || false,
        fromDate: initialMeeting.meetStartTime ? dayjs(initialMeeting.meetStartTime) : null,
        fromTime: initialMeeting.meetStartTime ? dayjs(initialMeeting.meetStartTime) : null,
        toDate: initialMeeting.meetEndTime ? dayjs(initialMeeting.meetEndTime) : null,
        toTime: initialMeeting.meetEndTime ? dayjs(initialMeeting.meetEndTime) : null,
        host: initialMeeting.host?.id
          ? String(initialMeeting.host.id)
          : "",
        participants: initialMeeting.participants || [],
        relatedTo: getFullName(leadDetails.first_name, leadDetails.middle_name, leadDetails.last_name) || "",
        description: initialMeeting.description || "",
        reminder: !!initialMeeting.reminder,
        reminderDate: initialMeeting.reminder ? dayjs(initialMeeting.reminder) : null,
        reminderHour: initialMeeting.reminder
          ? dayjs(initialMeeting.reminder).format("hh")
          : "",
        reminderMinute: initialMeeting.reminder
          ? dayjs(initialMeeting.reminder).format("mm")
          : "",
        reminderAmPm:
          initialMeeting.reminder &&
            dayjs(initialMeeting.reminder).format("A") === "PM"
            ? "PM"
            : "AM",
        repeat: !!initialMeeting.repeat,
        repeatType: initialMeeting.repeatType || "Daily",
        ends: initialMeeting.ends || "never",
        endsAfter: initialMeeting.endsAfter ?? 1,
        dueDate: initialMeeting.dueDate ? dayjs(initialMeeting.dueDate) : null,
      };
    } else {
      return {
        ...MEETING_DIALOG_INITIAL_VALUES,
        host: matchedUser?.value || "",
        relatedTo: getFullName(leadDetails.first_name, leadDetails.middle_name, leadDetails.last_name) || "",
      };
    }
  }, [initialMeeting, leadDetails, leadOptions]);

  const handleSave = async (values: any, options: { sendInvites: boolean; forceCreate?: boolean }) => {
    let payload;
    try {
      setLoading(true);

      // Format reminder time if enabled
      let reminderDateTime = null;
      if (values.reminder && values.reminderDate) {
        const hour = values.reminderHour || "00";
        const minute = values.reminderMinute || "00";
        const amPm = values.reminderAmPm || "AM";
        let hour24 = parseInt(hour, 10);

        if (amPm === "PM" && hour24 < 12) hour24 += 12;
        if (amPm === "AM" && hour24 === 12) hour24 = 0;

        reminderDateTime = dayjs(values.reminderDate)
          .hour(hour24)
          .minute(parseInt(minute))
          .second(0)
          .millisecond(0)
          .toISOString();
      }
      payload = {
        title: values.title,
        location: values.location,
        meetStartTime: getCombinedDateTime(values.fromDate, values.fromTime),
        meetEndTime: getCombinedDateTime(values.toDate, values.toTime),
        host_id: parseInt(values.host, 10),
        //relatedTo: values.relatedTo,
        description: values.description,
        reminder: reminderDateTime,
        all_day: values.allDay || false,
        relatedToLeadId: leadDetails?.id || null,
        participants: values.participants || null,
        showSendInvitationPrompt: options?.sendInvites
      };

      let response;
      if (initialMeeting?.id) {
        response = await editMeeting(initialMeeting.id, payload);
        setAlertMessage("Meeting updated successfully.");
      } else {
        response = await createMeeting(payload, "Lead Details");
        setAlertMessage("Meeting created successfully.");
      }

      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || values);
      onClose();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message;
      if (
        errorMsg &&
        errorMsg.includes("You have another meeting")
      ) {
        setPendingMeetingPayload(payload);
        setOverlapErrorMessage(errorMsg);
        setShowOverlapPopup(true);
      } else {
        setAlertMessage(
          errorMsg || "Failed to save meeting. Please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };
  const retryCreateWithIgnoreOverlap = async () => {
    if (!pendingMeetingPayload) return;

    try {
      setLoading(true);

      const updatedPayload = {
        ...pendingMeetingPayload,
        ignoreOverlap: true,
      };

      let response;
      if (initialMeeting?.id) {
        response = await editMeeting(initialMeeting.id, updatedPayload);
        setAlertMessage("Meeting updated successfully.");
      } else {
        response = await createMeeting(updatedPayload, "Lead Details");
        setAlertMessage("Meeting created successfully.");
      }

      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || pendingMeetingPayload);
      onClose();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
        "Failed to save meeting. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setShowOverlapPopup(false);
      setPendingMeetingPayload(null);
    }
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Lead Details");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default lead owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            host: matchedUser.value,
            relatedTo: getFullName(leadDetails.first_name, leadDetails.middle_name, leadDetails.last_name) || "",
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
          "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, [leadDetails]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // mobile ≤ 600px
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            onClose();
          }
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "640px",   // 90% width on mobile
              minWidth: isMobile ? "auto" : "640px",
              position: isMobile ? "fixed" : "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold !important" }}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={computedInitialValues}
          validationSchema={MEETING_DIALOG_VALIDATION_SCHEMA}
          onSubmit={(values) => {
            setFormData(values)
            if (values.participants && values.participants.length > 0) {
              setShowInvitePopup(true);
            } else {
              handleSave(values, { sendInvites: false });
            }
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched }) => {
            const handleParticipantsChange = (newEmails: string[]) => {
              setFieldValue("participants", newEmails);
            };
            return (
              <>
                <Form>

                  <AllDayAutoFillEffect />
                  <div className="meeting-dialog-container">
                    <DialogContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>Title</Typography>
                            <Box flex={1}>
                              <TextInputComponent
                                type="text"
                                fieldName="title"
                                placeholder="Enter Title"
                                optional={false}
                                disabled={false}
                                value={values.title}
                                onChange={handleChange}
                                maxlength="100"
                                isRequired={true}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>Location</Typography>
                            <Box flex={1}>
                              <TextInputComponent
                                type="text"
                                fieldName="location"
                                placeholder="Enter Location"
                                optional={false}
                                disabled={false}
                                value={values.location}
                                onChange={handleChange}
                                maxlength="100"
                              //isRequired={true}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>All Day</Typography>
                            <Box flex={1}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.allDay}
                                    onChange={(e) => setFieldValue("allDay", e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label={values.allDay ? "Yes" : "No"}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>From</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Box display="flex" gap={2} flex={1} alignItems="flex-start">
                                {/* Date Picker */}
                                <Box sx={{ minWidth: isMobile ? 80 : 180 }}>
                                  <DesktopDatePicker
                                    label="Select Date"
                                    value={values.fromDate}
                                    onChange={(newDate) => {
                                      setFieldValue('fromDate', newDate);

                                      // If allDay is toggled on, sync toDate with fromDate
                                      if (values.allDay && newDate) {
                                        setFieldValue('toDate', newDate);
                                      }
                                    }}
                                    // disabled={values.allDay}
                                    minDate={values.allDay ? dayjs(values.fromDate).startOf('day') : dayjs()}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        error: Boolean(touched.fromDate && errors.fromDate),
                                        helperText: touched.fromDate && errors.fromDate ? errors.fromDate : " ",
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Time Picker */}
                                <Box sx={{ minWidth: isMobile ? 80 : 180 }}>
                                  <DesktopTimePicker
                                    label="Select Time"
                                    value={values.fromTime}
                                    onChange={(newTime) => setFieldValue('fromTime', newTime)}
                                    disabled={values.allDay}
                                    minTime={
                                      dayjs(values.fromDate).isSame(dayjs(), 'day')
                                        ? roundToNext15Min(dayjs()) // if today, restrict to current time onward
                                        : dayjs().startOf('day')
                                    }
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        error: Boolean(touched.fromTime && errors.fromTime),
                                        helperText: touched.fromTime && errors.fromTime ? errors.fromTime : " ",
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </LocalizationProvider>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>To</Typography>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Box display="flex" gap={2} flex={1} alignItems="flex-start">
                                {/* Date Picker */}
                                <Box sx={{ minWidth: isMobile ? 80 : 180 }}>
                                  <DesktopDatePicker
                                    label="Select Date"
                                    value={values.toDate}
                                    onChange={(newDate) => {
                                      setFieldValue('toDate', newDate);

                                      // If allDay is toggled on, sync toDate with fromDate
                                      if (values.allDay && newDate) {
                                        setFieldValue('fromDate', newDate);
                                      }
                                    }}
                                    //disabled={values.allDay}
                                    minDate={values.allDay ? dayjs(values.fromDate).startOf('day') : dayjs()}
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        error: Boolean(touched.toDate && errors.toDate),
                                        helperText: touched.toDate && errors.toDate ? errors.toDate : " ", // reserve space
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Time Picker */}
                                <Box sx={{ minWidth: isMobile ? 80 : 180 }}>
                                  <TimePicker
                                    label="Select Time"
                                    value={values.toTime}
                                    onChange={(newTime) => setFieldValue('toTime', newTime)}
                                    disabled={values.allDay}
                                    minTime={
                                      dayjs(values.fromDate).isSame(dayjs(), 'day')
                                        ? roundToNext15Min(dayjs()) // if today, restrict to current time onward
                                        : dayjs().startOf('day')
                                    }
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        error: Boolean(touched.toTime && errors.toTime),
                                        helperText: touched.toTime && errors.toTime ? errors.toTime : " ", // reserve space
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </LocalizationProvider>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>Host</Typography>
                            <Box flex={1}>
                              <SearchableSelectOwner
                                fieldName="host"
                                options={leadOptions}
                                isShowError={!values?.host}
                                placeholder="Select Host"
                                optional={true}
                                value={
                                  leadOptions.find(
                                    (opt) => opt.value === values?.host
                                  ) || null
                                }
                                onChange={(selectedOption: OwnerOption | null) => {
                                  if (selectedOption) {
                                    setFieldValue(
                                      "host",
                                      selectedOption.value
                                    ); // saving only the ID
                                  } else {
                                    setFieldValue("host", null); // handle null case
                                  }
                                }}
                                handleBlur={() => {
                                  setFieldTouched("host", true);
                                }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>Participants</Typography>
                            <Box
                              flex={1}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              px={2}
                              py={1.5}
                              sx={{
                                height: "17px",
                                backgroundColor: "#f5f5f5",
                                //borderTop: "2px solid #e30000",
                                borderRadius: "4px 4px 0 0",
                                borderLeft: "none",
                                borderRight: "none",
                                borderBottom: "none",
                              }}
                            >
                              <Typography>
                                {Array.isArray(values.participants) && values.participants.length > 0
                                  ? `Selected ${values.participants.length}`
                                  : ""}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#1194DA",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                }}
                                onClick={() => setParticipantsDialogOpen(true)}
                              >
                                Add +
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6} className="campaign-input-component">
                            <Typography width={100}>Related to</Typography>
                            <Box flex={1}>
                              <TextField
                                name={"relatedTo"}
                                placeholder="Select Leads"
                                id="relatedTo"
                                value={values.relatedTo}
                                onChange={handleChange}
                                type="text"
                                disabled
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={6}>
                            <Typography width={100}>Description</Typography>
                            <Box flex={1}>
                              <TextInputComponent
                                type="text"
                                fieldName="description"
                                placeholder="Add Description"
                                optional={false}
                                disabled={false}
                                value={values.description}
                                onChange={handleChange}
                                maxlength="150"
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box display="flex" alignItems="center" gap={5}>
                            {/* Label */}
                            <Typography width={100}>Reminder</Typography>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {/* Toggle */}
                                <Switch
                                  checked={values.reminder}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFieldValue("reminder", checked);

                                    if (!checked) {
                                      // Clear all reminder-related fields
                                      setFieldValue("reminderDate", null);
                                      setFieldValue("reminderHour", "");
                                      setFieldValue("reminderMinute", "");
                                      setFieldValue("reminderAmPm", "AM");
                                    }
                                  }}
                                />

                                {/* Only show date & time inputs when reminder is ON */}
                                {values.reminder && (
                                  <>
                                    {/* Date */}
                                    <Box
                                      sx={{ width: 150, marginRight: "5px" }}
                                      className="date-remainder"
                                    >
                                      <DatePickerComponent
                                        label="Date"
                                        fieldName="reminderDate"
                                        value={values.reminderDate}
                                        onChange={(date) => {
                                          setFieldValue("reminderDate", date);
                                          if (date && dayjs(date).isSame(dayjs(), "day")) {
                                            // Set defaults to next 30 minutes if selected date is today
                                            const now = dayjs();
                                            const next30 = now.add(30, "minute");
                                            let hour = next30.hour();
                                            let ampm = hour >= 12 ? "PM" : "AM";
                                            let hour12 = hour % 12;
                                            if (hour12 === 0) hour12 = 12;
                                            let minute = next30.minute();
                                            setFieldValue("reminderHour", hour12.toString().padStart(2, "0"));
                                            setFieldValue("reminderMinute", minute.toString().padStart(2, "0"));
                                            setFieldValue("reminderAmPm", ampm);
                                          } else {
                                            // Clear time fields for future dates
                                            setFieldValue("reminderHour", "");
                                            setFieldValue("reminderMinute", "");
                                            setFieldValue("reminderAmPm", "AM");
                                          }
                                        }}   
                                        minDate={dayjs()}
                                        optional={true}
                                        isLabel={false}
                                        error={!!(touched.reminderDate && errors.reminderDate)}
                                        useFormikError={false}
                                      />
                                    </Box>

                                    {/* Hour */}
                                    <Autocomplete
                                      freeSolo
                                      className="reminder-minute-autocomplete"
                                      disableClearable
                                      options={Array.from({ length: 12 }, (_, i) =>
                                        (i + 1).toString().padStart(2, "0")
                                      )}
                                      getOptionDisabled={(option) => {
                                        // Disable past hours if reminderDate is today
                                        if (values.reminderDate && dayjs(values.reminderDate).isSame(dayjs(), "day")) {
                                          let selectedAmPm = values.reminderAmPm || "AM";
                                          let now = dayjs();
                                          let currentHour = now.hour();
                                          let optionHour = parseInt(option, 10);
                                          // Convert to 24h for comparison
                                          if (selectedAmPm === "PM" && optionHour < 12) optionHour += 12;
                                          if (selectedAmPm === "AM" && optionHour === 12) optionHour = 0;
                                          return optionHour < currentHour;
                                        }
                                        return false;
                                      }}
                                      value={
                                        values.reminderHour
                                          ?.toString()
                                          .padStart(2, "0") || ""
                                      }
                                      onInputChange={(event, newInputValue) => {
                                        // Allow only digits, and limit to 2 characters
                                        if (/^\d{0,2}$/.test(newInputValue)) {
                                          setFieldValue(
                                            "reminderHour",
                                            newInputValue
                                          );
                                        }
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          name="reminderHour"
                                          type="text"
                                          inputProps={{
                                            ...params.inputProps,
                                            style: { width: 50, textAlign: "center" },
                                            maxLength: 2,
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            onWheel: (e) => e.currentTarget.blur(), // Prevent scroll on wheel
                                          }}
                                        />
                                      )}
                                    />

                                    <Typography sx={{ marginRight: "5px" }}>
                                      :
                                    </Typography>

                                    <Autocomplete
                                      className="reminder-minute-autocomplete"
                                      freeSolo
                                      disableClearable
                                      options={Array.from({ length: 60 }, (_, i) =>
                                        i.toString().padStart(2, "0")
                                      )}
                                      getOptionDisabled={(option) => {
                                        // Disable past minutes if reminderDate is today and hour is current hour
                                        if (values.reminderDate && dayjs(values.reminderDate).isSame(dayjs(), "day")) {
                                          let selectedAmPm = values.reminderAmPm || "AM";
                                          let selectedHour = parseInt(values.reminderHour || "0", 10);
                                          let now = dayjs();
                                          let currentHour = now.hour();
                                          // Convert to 24h for comparison
                                          if (selectedAmPm === "PM" && selectedHour < 12) selectedHour += 12;
                                          if (selectedAmPm === "AM" && selectedHour === 12) selectedHour = 0;
                                          if (selectedHour === currentHour) {
                                            let currentMinute = now.minute();
                                            return parseInt(option, 10) < currentMinute;
                                          }
                                        }
                                        return false;
                                      }}
                                      value={
                                        values.reminderMinute
                                          ?.toString()
                                          .padStart(2, "0") || ""
                                      }
                                      onInputChange={(event, newInputValue) => {
                                        if (/^\d{0,2}$/.test(newInputValue)) {
                                          setFieldValue(
                                            "reminderMinute",
                                            newInputValue
                                          );
                                        }
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          name="reminderMinute"
                                          type="text"
                                          inputProps={{
                                            ...params.inputProps,
                                            style: { width: 50, textAlign: "center" },
                                            maxLength: 2,
                                            inputMode: "numeric",
                                            pattern: "[0-9]*",
                                            onWheel: (e) => e.currentTarget.blur(),
                                          }}
                                        />
                                      )}
                                    />
                                    {/* AM/PM */}
                                    <FormControl size="small">
                                      <Select
                                        name="reminderAmPm"
                                        value={values.reminderAmPm || "AM"}
                                        onChange={(e: any) => {
                                          handleChange(e);
                                        }}
                                        sx={{
                                          width: 70,
                                          "& .MuiSelect-select": {
                                            paddingRight: "0 !important", // remove padding right forcibly
                                          },
                                        }}
                                      >
                                        <MenuItem value="AM">AM</MenuItem>
                                        <MenuItem value="PM">PM</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </>
                                )}
                              </Box>
                              {values.reminder && (
                                (touched.reminderHour && errors.reminderHour) ? (
                                  <div className="error-message">{errors.reminderHour}</div>
                                ) : (touched.reminderDate && errors.reminderDate) ? (
                                  <div className="error-message">{errors.reminderDate}</div>
                                ) : null
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </DialogContent>

                    <DialogActions sx={{ margin: "12px" }}>
                      <button onClick={onClose} className="cancel-button" >Cancel</button>
                      <button type="submit" className="cancel-button next-button-wrapper">
                        Save
                      </button>
                    </DialogActions>
                  </div>
                </Form>
                <ParticipantsDialog
                  open={participantsDialogOpen}
                  onClose={() => setParticipantsDialogOpen(false)}
                  emails={values.participants || []}
                  setEmails={handleParticipantsChange}
                />
              </>
            )
          }}

        </Formik>
        <ConfirmationPopup
          title={overlapErrorMessage}
          open={showOverlapPopup}
          note={"Are you sure you want to create this meeting anyway?"}
          onCancel={() => {
            setShowInvitePopup(false);
            setShowOverlapPopup(false)
            setPendingMeetingPayload(null);
          }}
          onOkay={retryCreateWithIgnoreOverlap}
          buttonText="Create"
          Icon={ConfirmIcon}
        />
        <ConfirmationPopup
          title="Send Invite"
          open={showInvitePopup}
          note={"Would you like to send invitations to guests?"}
          onCancel={() => {
            setShowInvitePopup(false)
          }}
          onOkay={() => {
            setShowInvitePopup(false)
            handleSave(formData, { sendInvites: true });
          }}
          buttonText="Send"
          isDoNotSendButtonRequired={true}
          extraButtonText="Do Not Send"
          onExtraButtonOkay={() => {
            setShowInvitePopup(false);
            // Do not send invitations (false)
            handleSave(formData, { sendInvites: false });
          }}
          Icon={ConfirmIcon}
        />
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default CommonMeetingDialog;
